import React, { useEffect, useRef } from "react";
// eslint-disable-next-line no-restricted-imports
import * as Dropdown from "@radix-ui/react-dropdown-menu";
import { colors, styled } from "../../stitches.config";
import { ExpandableCellContainer } from "./__styles__/ExpandableCell";
import Pill, { PillProps } from "./Pill";

export const ExpandableCell = ({
  pills,
  clickable = true,
}: {
  pills?: Array<PillProps>;
  clickable?: boolean;
}) => {
  const [open, setOpen] = React.useState(false);
  const cellRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = (event: Event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    window.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, []);

  if (!pills) return null;

  const CellButton = () => {
    const handleContainerClick = (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      const pillContainer = target.closest("[data-pill-container]");

      if (pillContainer) {
        // If dropdown is closed, open it on first pill click
        if (!open) {
          if (clickable) {
            setOpen(true);
          }
          return;
        }
        // If dropdown is open, let the pill handle its own click
        return;
      }

      if (clickable) {
        setOpen(true);
      }
    };

    return (
      <ExpandableCellContainer
        onClick={handleContainerClick}
        clickDisabled={!clickable}
        ref={cellRef}
        data-testid="expanding-cell"
      >
        {pills.map((pill, index) => (
          <Pill
            key={index}
            label={pill.label}
            iconName={pill.iconName}
            href={pill.href}
            to={pill.to}
            asDropdownItem={false}
            color={pill.color}
            iconColor={pill.iconColor}
            onClick={pill.onClick}
          />
        ))}
      </ExpandableCellContainer>
    );
  };

  return (
    <>
      <CellButton />
      <Dropdown.Root modal={false} open={open} onOpenChange={setOpen}>
        <Dropdown.Trigger asChild tabIndex={0}>
          <div />
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <DropdownContent
            ref={dropdownRef}
            style={{
              width: cellRef.current?.offsetWidth! + 16,
              top: -cellRef.current?.offsetHeight! - 8,
            }}
            side="bottom"
            sideOffset={-cellRef.current?.offsetHeight! - 8}
            collisionPadding={16}
          >
            <ExpandableCellContainer data-testid="expanded-cell" expanded>
              {pills.map((pill, index) => (
                <Pill
                  key={index}
                  label={pill.label}
                  iconName={pill.iconName}
                  href={pill.href}
                  to={pill.to}
                  asDropdownItem={true}
                  onClick={pill.onClick}
                />
              ))}
            </ExpandableCellContainer>
          </DropdownContent>
        </Dropdown.Portal>
      </Dropdown.Root>
    </>
  );
};

// This has to be in the same file and cannot live in the __styles__ file
// Moving to the styles file will break the dropdown
const DropdownContent = styled(Dropdown.Content, {
  background: colors.bgUiSurface,
  border: 1,
  borderColor: colors.borderUiFocused,
  borderStyle: "solid",
  borderRadius: 4,
  zIndex: 2,
});
